import { useCallback } from 'react';

import { useMutation, useQueryClient } from '@mint-lib/query-client';
import * as DataIngestion from '@mint-sdk/api-data-ingestion';

import * as API from '../api/index.js';
import type {
  GranularityLevel,
  NormalizedTemplateCollaborator,
  PastImportsSearchParams,
  TemplateCollaborator,
} from '../api/types.js';
import { getPaginatedDataPlaceholder } from '../constants/tablesConfig.js';
import { useImportHubV2 } from '../hooks/ff/index.js';
import { useEnhancedMutation, useTypedQuery } from '../hooks/index.js';
import { QueryKeys } from './queryClient.js';

export const useTemplatesListQueryOptions = () => {
  const isImportHubV2 = useImportHubV2();

  return (
    isImportHubV2
      ? DataIngestion.Templates.getTemplatesListV2QueryOptions
      : DataIngestion.Templates.getTemplatesListV1QueryOptions
  ) as typeof DataIngestion.Templates.getTemplatesListV1QueryOptions;
};

export const useTemplateFilterOptionsQueryOptions = () => {
  const isImportHubV2 = useImportHubV2();

  return (
    isImportHubV2
      ? DataIngestion.Templates.getTemplateFilterOptionsV2QueryOptions
      : DataIngestion.Templates.getTemplateFilterOptionsV1QueryOptions
  ) as typeof DataIngestion.Templates.getTemplateFilterOptionsV1QueryOptions;
};

export const useTemplates = (
  searchParams: PastImportsSearchParams,
  options?: { throwOnError?: boolean },
) => {
  const getQueryOptions = useTemplatesListQueryOptions();

  const { filters, ...restSearchParams } = searchParams;

  const params = {
    ...filters,
    ...restSearchParams,
  };

  const queryOptions = getQueryOptions({ params });

  return useTypedQuery({
    ...queryOptions,
    placeholderData: getPaginatedDataPlaceholder(),
    staleTime: 30 * 1000,
    ...options,
  });
};

export const useTemplatePreview = (templateId: string) => {
  const isImportHubV2 = useImportHubV2();

  const templatePreviewV1QueryOption =
    DataIngestion.Templates.getTemplatePreviewV1QueryOptions({
      urlParams: { templateId },
    });
  const templatePreviewV2QueryOption =
    DataIngestion.Templates.getTemplatePreviewV2QueryOptions({
      urlParams: { templateId },
    });
  const templatePreviewQueryOption = (
    isImportHubV2 ? templatePreviewV2QueryOption : templatePreviewV1QueryOption
  ) as typeof templatePreviewV1QueryOption;

  return useTypedQuery({
    ...templatePreviewQueryOption,
    refetchOnMount: false,
  });
};

export function useTemplateMetricsQueryOptions({
  granularityLevel,
  activityId,
}: {
  granularityLevel: GranularityLevel;
  activityId?: string;
}) {
  const isImportHubV2 = useImportHubV2();

  const v1QueryOptions =
    DataIngestion.SystemMetrics.getSystemMetricsQueryOptions({
      params: {
        granularityLevelType: granularityLevel,
      },
    });

  const v2QueryOptions =
    DataIngestion.SystemMetrics.getTemplateMetricsQueryOptions({
      urlParams: { activityId: activityId ?? '' },
      params: { granularityLevel },
    });

  const queryOptions = (
    isImportHubV2 ? v2QueryOptions : v1QueryOptions
  ) as typeof v1QueryOptions;

  return queryOptions;
}

export function useTemplateMetrics(
  {
    granularityLevel,
    activityId,
  }: { granularityLevel: GranularityLevel; activityId?: string },
  options?: { throwOnError?: boolean },
) {
  const queryOptions = useTemplateMetricsQueryOptions({
    granularityLevel,
    activityId,
  });

  return useTypedQuery({
    ...queryOptions,
    select(data) {
      return {
        systemMetrics: data.items,
        customMetrics: data.additional.customMetrics,
        maxMetricsCount: data.additional.maxCount ?? Infinity,
      };
    },
    placeholderData: {
      items: [],
      additional: { customMetrics: [], maxCount: Infinity },
    },
    ...options,
  });
}

export const useTemplateCollaborators = (templateId: string) => {
  const templateCollaboratorsQueryKey =
    QueryKeys.template.collaborators(templateId);

  const selector = useCallback((collaborators: TemplateCollaborator[]) => {
    const normalizedCollaborators: NormalizedTemplateCollaborator[] =
      collaborators.map(({ id, userId, ...rest }) => ({
        id: userId,
        collaboratorId: id,
        ...rest,
      }));
    return {
      list: normalizedCollaborators,
      byId: normalizedCollaborators.reduce(
        (acc, collaborator) => {
          acc[collaborator.id] = collaborator;
          return acc;
        },
        {} as Record<number, NormalizedTemplateCollaborator>,
      ),
    };
  }, []);

  return useTypedQuery({
    queryKey: templateCollaboratorsQueryKey,
    queryFn: async () => {
      const collaborators = await API.getTemplateCollaborators(templateId);
      return collaborators;
    },
    select: selector,
    placeholderData: [],
  });
};

export const useCreateTemplateMutation = () => {
  return useMutation({
    mutationFn: API.createTemplate,
  });
};

export const useDeleteTemplateMutation = () => {
  return useEnhancedMutation({
    mutationFn: API.deleteTemplate,
    meta: {
      getErrorsMap(t) {
        return {
          default: t('Failed to delete templete'),
        };
      },
    },
  });
};

export const useSetTemplateCollaborators = (templateId: string) => {
  const queryClient = useQueryClient();

  const templateCollaboratorsQueryKey =
    QueryKeys.template.collaborators(templateId);

  return useEnhancedMutation({
    mutationFn: (payload: Array<TemplateCollaborator & { email: string }>) =>
      API.setTemplateCollaborators(templateId, payload),
    onSuccess: () => {
      queryClient.removeQueries({
        queryKey: ['templatesList'],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: templateCollaboratorsQueryKey,
      });
    },
    meta: {
      getErrorsMap(t) {
        return {
          default: t('Failed to assign template collaborators'),
        };
      },
    },
  });
};

export const useCreateTemplate = () => {
  const queryClient = useQueryClient();
  const isImportHubV2 = useImportHubV2();

  const createTemplateFn = isImportHubV2
    ? DataIngestion.Templates.createTemplateV2
    : DataIngestion.Templates.createTemplate;

  return useEnhancedMutation({
    mutationFn: createTemplateFn,
    onSuccess: () => {
      queryClient.removeQueries({
        queryKey: ['templatesList'],
        exact: false,
      });
    },
  });
};
